import { Button } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { Badge } from '@neo/primitives/badge'
import { TruncatedTextWithTooltip } from '@neo/primitives/truncated-text-with-tooltip'
import { Handle, Position } from '@xyflow/react'
import { Layers, MoreVertical } from 'lucide-react'
import { FlowSourceTypes, FlowTypes, SlackTriggerEventText } from 'v2/types'
import { StartNodeHandle } from '../components/start-node-handle'
import { FlowTriggerConfigMap } from '../constants'
import { useStartNodeType } from '../hooks/use-start-node-type'
import { useFlowStore } from '../store'
import type { StartNodeData } from '../types'

interface StartProps {
  id: string
  data: StartNodeData
}

export const Start = ({ id, data }: StartProps) => {
  const startNodeType = useStartNodeType()
  const isOmni = startNodeType === 'omni'
  const displayFlowType = isOmni ? FlowTypes.incoming_call : startNodeType
  const isEditing = useFlowStore((state) => state.isEditing)
  const selectedID = useFlowStore((state) => state.selectedNodeID)
  const isSelected = selectedID === id

  const getDisabledReason = () => {
    if (!isEditing) return "Can't edit trigger node when flow is not editing"
    return undefined
  }
  const isSlack = displayFlowType === FlowTypes.incoming_slack

  return (
    <div
      className={cn(
        'flow-node group relative rounded-2xl node-hover-gradient p-[1.2px]',
        isSelected && 'selected',
        data.meta.shouldHighlight && 'node-currently-executing',
      )}
      data-hover="true"
    >
      <div
        className={cn(
          'flex flex-col gap-1 w-[236px] h-full rounded-2xl border border-st2 bg-background transition-colors relative z-10 hover:border-transparent',
          (isSelected || data.meta.shouldHighlight) && 'border-transparent',
        )}
      >
        <div className="min-h-8 p-3 rounded-xl text-s-500 gap-2 flex flex-col">
          <Badge className="w-fit rounded-sm bg-[var(--trigger-bg)] p-1 py-0.5 pt-1 font-jetbrains text-label uppercase tracking-normal text-[var(--trigger-fg)] hover:bg-[var(--trigger-bg)]">
            TRIGGER
          </Badge>
          {isOmni ? (
            <>
              <div className="flex items-center">
                <div className="flex items-center gap-2 bg-bg2 pr-3 rounded-md overflow-hidden">
                  <div className="rounded p-1 bg-accent/10">
                    <Layers size={16} />
                  </div>
                  <div className="font-inter text-p-500">Start</div>
                </div>
              </div>
              <span className="font-inter text-s-400 text-secondary">
                Triggers for multiple channel types
              </span>
            </>
          ) : (
            <>
              <div className="flex items-center">
                <div className="flex items-center gap-2 bg-bg2 pr-3 rounded-md overflow-hidden">
                  <div className={`rounded p-1 ${FlowTriggerConfigMap[displayFlowType].class}`}>
                    {FlowTriggerConfigMap[displayFlowType].icon}
                  </div>
                  <TruncatedTextWithTooltip
                    tooltipContent={FlowTriggerConfigMap[displayFlowType].title}
                    className="font-inter text-p-500"
                  >
                    {isSlack
                      ? data.config.slack_trigger_event
                        ? SlackTriggerEventText[
                            data.config.slack_trigger_event as keyof typeof SlackTriggerEventText
                          ]?.title
                        : FlowTriggerConfigMap[displayFlowType].title
                      : FlowTriggerConfigMap[displayFlowType].title}
                  </TruncatedTextWithTooltip>
                </div>
                <div className="ml-auto nodrag">
                  <Button
                    variant="ghost"
                    size="iconXs"
                    className="pl-0"
                    disabledReason={getDisabledReason()}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      const rect = e.currentTarget.getBoundingClientRect()
                      const customEvent = new MouseEvent('contextmenu', {
                        bubbles: true,
                        cancelable: true,
                        clientX: rect.left + 20,
                        clientY: rect.bottom,
                      })
                      e.currentTarget.dispatchEvent(customEvent)
                    }}
                  >
                    <MoreVertical size={16} className="text-secondary" />
                  </Button>
                </div>
              </div>
              <span className="font-inter text-s-400 text-secondary">
                {FlowTriggerConfigMap[displayFlowType].description}
              </span>
            </>
          )}
        </div>
        {isOmni ? (
          Object.values(FlowTypes).map((ft) => (
            <Handle
              key={ft}
              id={FlowSourceTypes[ft]}
              type="source"
              position={Position.Bottom}
              isConnectable={false}
              className="!bottom-0 !left-1/2 !-translate-x-1/2 !size-0 !border-0 !bg-transparent"
            />
          ))
        ) : (
          <StartNodeHandle type={displayFlowType} />
        )}
      </div>
    </div>
  )
}
